import { DataAccess } from "../../../dataaccess/data.access";
import { ContactService } from "../../contact.service";
import { S25Util } from "../../../util/s25-util";
import { Cache, Invalidate } from "../../../decorators/cache.decorator";
import { SubscriberSet } from "./SubscriberSet";
import { Item } from "../../../pojo/Item";
import { SearchCriteria } from "../../../pojo/SearchCriteria";
import { SubscriberSetUtil } from "./subscriber.set.util";
import { S25Const } from "../../../util/s25-const";
import { FavoriteService } from "../../favorite.service";
import { SearchUtil } from "../s25.search.util";

export class S25SubscriberSetService {
    public static async getAllSets(): Promise<SubscriberSet.Set<SubscriberSet.Filter>[]> {
        const sets = await DataAccess.get(
            DataAccess.injectCaller("/micro/subscription/list.json", "S25SubscriberSetService.getAllSets"),
        );
        return sets?.content?.data?.items ?? [];
    }

    public static async getSetById(id: string): Promise<SubscriberSet.Set<SubscriberSet.Filter>[]> {
        const sets = await DataAccess.get(`/micro/subscription/${id}/details.json`);
        return sets?.content?.data?.items ?? [];
    }

    /**
     * Get full details of a single subscriber set
     * Used for loading a set to edit in the search builder
     */
    public static async getSetDetails(id: number): Promise<SubscriberSet.Set<SubscriberSet.Filter>> {
        const response = await DataAccess.get(
            DataAccess.injectCaller(`/micro/subscription/${id}/detail.json`, "S25SubscriberSetService.getSetDetails"),
        );
        const items = response?.content?.data?.items ?? [];
        return items[0];
    }

    //TODO: see if subscription/list can do this with parameters
    @Cache({ targetName: "S25SubscriberSetService", immutable: true })
    public static async getCurrentUserSets(typeId?: Item.Id): Promise<SubscriberSet.Set<SubscriberSet.Filter>[]> {
        const currentUsername = await ContactService.getCurrentUsername();
        const sets = await S25SubscriberSetService.getAllSets();
        return sets.filter(
            (set) => currentUsername === set.owner && (S25Util.isUndefined(typeId) || typeId === set.objectType),
        );
    }

    @Invalidate({ serviceName: "S25SubscriberSetService", methodName: "getCurrentUserSets" })
    public static async deleteSet(id: number, typeId?: Item.Id): Promise<void> {
        await DataAccess.delete(
            DataAccess.injectCaller(
                `/micro/subscription/list.json?subscriber_key=${id}`,
                "S25SubscriberSetService.deleteSet",
            ),
        );

        if (S25Util.isDefined(typeId)) {
            FavoriteService.remove({ itemId: id, itemTypeId: SearchUtil.itemType2SearchTypeId(typeId) });
        }
    }

    @Invalidate({ serviceName: "S25SubscriberSetService", methodName: "getCurrentUserSets" })
    @Invalidate({ serviceName: "S25SubscriberSetService", methodName: "getSetDetails" })
    public static async createSet(
        name: string,
        typeId: Item.Id,
        filter: SubscriberSet.Filter,
        isFavorite?: boolean,
    ): Promise<SubscriberSet.Set<SubscriberSet.Filter>> {
        const payload = {
            content: {
                apiVersion: "0.1",
                data: [
                    {
                        name: name,
                        objectTypeName: S25Const.itemId2Name[typeId],
                        filterParams: filter,
                    },
                ],
            },
        };

        const [resp, error] = await S25Util.Maybe(
            DataAccess.post(
                DataAccess.injectCaller(`/micro/subscription/new.json`, "S25SubscriberSetService.createSet"),
                payload,
            ),
        );
        if (error) {
            return Promise.reject(error);
        }

        const set = resp?.content?.data?.items[0];
        if (isFavorite) {
            FavoriteService.add({ itemId: set.id, itemTypeId: SearchUtil.itemType2SearchTypeId(typeId) });
            set.isFavorite = true;
        }

        return set;
    }

    /**
     * Update an existing subscriber set
     * Used when saving changes to an existing search
     */
    @Invalidate({ serviceName: "S25SubscriberSetService", methodName: "getCurrentUserSets" })
    @Invalidate({ serviceName: "S25SubscriberSetService", methodName: "getSetDetails" })
    public static async updateSet(
        id: number,
        name: string,
        typeId: Item.Id,
        filter: SubscriberSet.Filter,
        isFavorite?: boolean,
    ): Promise<SubscriberSet.Set<SubscriberSet.Filter>> {
        const payload = {
            content: {
                apiVersion: "0.1",
                data: [
                    {
                        id: id,
                        name: name,
                        objectTypeName: S25Const.itemId2Name[typeId],
                        filterParams: filter,
                    },
                ],
            },
        };

        const [resp, error] = await S25Util.Maybe(
            DataAccess.put(
                DataAccess.injectCaller(`/micro/subscription/${id}/detail.json`, "S25SubscriberSetService.updateSet"),
                payload,
            ),
        );
        if (error) {
            return Promise.reject(error);
        }

        const set = resp?.content?.data?.items[0];
        if (S25Util.isDefined(isFavorite)) {
            if (isFavorite) {
                FavoriteService.add({ itemId: set.id, itemTypeId: SearchUtil.itemType2SearchTypeId(typeId) });
            } else {
                FavoriteService.remove({ itemId: set.id, itemTypeId: SearchUtil.itemType2SearchTypeId(typeId) });
            }
            set.isFavorite = isFavorite;
        }

        return set;

        return resp.content?.data?.items[0];
    }

    public static async createSetFromStepModel(
        stepModel: SearchCriteria.Model,
        typeId: Item.Id,
        rootName: string,
        isFavorite?: boolean,
    ) {
        const filter = SubscriberSetUtil.getFilterFromStep(stepModel.step);
        if (stepModel.query_id) {
            return S25SubscriberSetService.updateSet(Number(stepModel.query_id), rootName, typeId, filter, isFavorite);
        } else {
            return S25SubscriberSetService.createSet(rootName, typeId, filter, isFavorite);
        }
    }
}
